import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ref } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { updateScoutConfiguration, refineScoutDetails } from '../scoutApi';
import { getAndCacheFirebaseValue } from '../firebaseDataCache';
import ScoutSetupForm, { answersFromScout } from '../components/ScoutSetupForm';
import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import { useLanguage } from '../contexts/LanguageContext';

export default function ConfigureScoutScreen({
  user,
  profile,
  detailsOnly = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { language } = useLanguage();
  const scoutId = profile?.primaryScoutId;
  const profileScout = profile?.jobScouts?.[scoutId];
  const [scout, setScout] = useState(profileScout || null);
  const [ready, setReady] = useState(Boolean(profileScout));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (profileScout) {
      setScout(profileScout);
      setReady(true);
      return;
    }
    if (!user?.uid || !scoutId) {
      setReady(true);
      return;
    }
    const path = `people/${user.uid}/jobScouts/${scoutId}`;
    getAndCacheFirebaseValue(ref(db, path), path)
      .then((snapshot) => {
        if (!cancelled) setScout(snapshot.exists() ? snapshot.val() : null);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            language === 'ar'
              ? 'تعذر تحميل المساعد. حاول مجدداً.'
              : 'Could not load Scout. Please try again.',
          );
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [profileScout, user?.uid, scoutId, language]);
  const save = async (answers) => {
    setSaving(true);
    try {
      await (detailsOnly ? refineScoutDetails : updateScoutConfiguration)({
        user,
        scoutId,
        ...answers,
      });
      navigate('/opportunities', {
        replace: true,
        state: { scoutUpdateQueued: true },
      });
    } finally {
      setSaving(false);
    }
  };
  const existing = scout ? { ...answersFromScout(scout), ...(location.state?.targetRole && !detailsOnly ? { targetDirection: location.state.targetRole } : {}) } : {};
  // Older nationwide US Scouts must choose states before any update can run.
  const missingStates =
    existing.targetCountries?.includes('United States') &&
    !existing.targetStates?.length;
  return (
    <main className="js-onboarding">
      {!detailsOnly && <div className="js-focus-plan-actions"><PlanControlSlot /></div>}
      {!ready && (
        <p role="status">
          {language === 'ar' ? 'جارٍ تحميل المساعد…' : 'Loading Scout…'}
        </p>
      )}
      {ready && scout && (
        <ScoutSetupForm
          key={scoutId}
          editing
          detailsOnly={detailsOnly}
          initialAnswers={existing}
          extractedProfile={scout}
          busy={saving}
          onSubmit={save}
          onBack={() => navigate('/opportunities')}
          initialStep={
            missingStates ? 1 : params.get('section') === 'details' ? 2 : 0
          }
        />
      )}
      {ready && !scout && (
        <p role="alert">
          {error ||
            (language === 'ar'
              ? 'ما زال إعداد المساعد جارياً. عُد إلى الفرص وحاول بعد قليل.'
              : 'Scout is still being prepared. Return to opportunities and try again shortly.')}
        </p>
      )}
    </main>
  );
}
